import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import MonthFilter from '../components/MonthFilter';
import TableScrollContainer from '../components/TableScrollContainer';
import { buildMonthOptions, matchesSelectedMonths } from '../utils/monthFilters';
import { formatCurrency } from '../utils/currency';
import './Relatorios.css';

function Relatorios() {
  const [receitas, setReceitas] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState([]);

  useEffect(() => {
    fetchRelatorios();
  }, []);

  const fetchRelatorios = async () => {
    try {
      const [receitasResponse, despesasResponse] = await Promise.all([
        axios.get('/api/lancamentos-receita'),
        axios.get('/api/lancamentos-despesa')
      ]);

      setReceitas(receitasResponse.data);
      setDespesas(despesasResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error);
      setLoading(false);
    }
  };

  const monthOptions = useMemo(() => {
    return buildMonthOptions([
      ...receitas.map((item) => item.data_competencia || item.data_lancamento),
      ...despesas.map((item) => item.data_competencia || item.data_lancamento),
    ]);
  }, [receitas, despesas]);

  const receitasFiltradas = useMemo(() => {
    return receitas.filter((item) => matchesSelectedMonths(item.data_competencia || item.data_lancamento, selectedMonths));
  }, [receitas, selectedMonths]);

  const despesasFiltradas = useMemo(() => {
    return despesas.filter((item) => matchesSelectedMonths(item.data_competencia || item.data_lancamento, selectedMonths));
  }, [despesas, selectedMonths]);

  const faturamento = useMemo(() => {
    const grouped = receitasFiltradas.reduce((accumulator, item) => {
      const dateKey = item.data_competencia || new Date(item.data_lancamento).toISOString().slice(0, 10);

      if (!accumulator[dateKey]) {
        accumulator[dateKey] = {
          data: dateKey,
          total_servicos: 0,
          total_bruto: 0,
          total_comissao: 0,
          total_liquido: 0,
        };
      }

      accumulator[dateKey].total_servicos += 1;
      accumulator[dateKey].total_bruto += Number(item.valor || 0);
      accumulator[dateKey].total_comissao += Number(item.valor || 0) * 0.2;
      accumulator[dateKey].total_liquido += Number(item.valor || 0) * 0.8;

      return accumulator;
    }, {});

    return Object.values(grouped).sort((a, b) => b.data.localeCompare(a.data));
  }, [receitasFiltradas]);

  const lucro = useMemo(() => {
    const totalReceita = receitasFiltradas.reduce((sum, item) => sum + Number(item.valor || 0), 0);
    const totalComissao = receitasFiltradas.reduce((sum, item) => sum + (Number(item.valor || 0) * 0.2), 0);
    const totalDespesa = despesasFiltradas.reduce((sum, item) => sum + Number(item.valor || 0), 0);

    return {
      total_receita: totalReceita,
      total_comissao: totalComissao,
      total_despesa: totalDespesa,
      lucro_liquido: totalReceita - totalDespesa,
    };
  }, [despesasFiltradas, receitasFiltradas]);

  const totalizarFaturamento = () => {
    return faturamento.reduce((sum, item) => sum + (parseFloat(item.total_bruto) || 0), 0);
  };

  const totalizarComissao = () => {
    return faturamento.reduce((sum, item) => sum + (parseFloat(item.total_comissao) || 0), 0);
  };

  if (loading) {
    return <p className="loading">Carregando relatórios...</p>;
  }

  return (
    <div className="relatorios">
      <h1>Relatórios</h1>
      <MonthFilter
        title="Filtrar meses"
        options={monthOptions}
        selectedMonths={selectedMonths}
        onChange={setSelectedMonths}
      />

      <div className="relatorio-section">
        <h2>Resumo do Período</h2>
        <div className="resumo-grid">
          <div className="resumo-card receita-card">
            <h3>Receita Bruta</h3>
            <p className="valor">{formatCurrency(lucro?.total_receita || 0)}</p>
          </div>
          <div className="resumo-card comissao-card">
            <h3>Comissão (20%)</h3>
            <p className="valor">{formatCurrency(lucro?.total_comissao || 0)}</p>
          </div>
          <div className="resumo-card despesa-card">
            <h3>Despesas</h3>
            <p className="valor">{formatCurrency(lucro?.total_despesa || 0)}</p>
          </div>
          <div className="resumo-card lucro-card">
            <h3>Lucro Líquido</h3>
            <p className="valor">{formatCurrency(lucro?.lucro_liquido || 0)}</p>
          </div>
        </div>
      </div>

      <div className="relatorio-section">
        <h2>Faturamento Diário</h2>
        <div className="card">
          <TableScrollContainer rows={faturamento.length}>
            <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Serviços</th>
                <th>Total Bruto</th>
                <th>Comissão</th>
                <th>Valor Líquido</th>
              </tr>
            </thead>
            <tbody>
              {faturamento.map((item, index) => (
                <tr key={index}>
                  <td><strong>{new Date(`${item.data}T00:00:00`).toLocaleDateString()}</strong></td>
                  <td>{item.total_servicos}</td>
                  <td>{formatCurrency(item.total_bruto)}</td>
                  <td className="comissao">{formatCurrency(item.total_comissao)}</td>
                  <td>{formatCurrency(item.total_liquido)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan="2">
                  <strong>TOTAL</strong>
                </td>
                <td>
                  <strong>{formatCurrency(totalizarFaturamento())}</strong>
                </td>
                <td className="comissao">
                  <strong>{formatCurrency(totalizarComissao())}</strong>
                </td>
                <td>
                  <strong>{formatCurrency(totalizarFaturamento() - totalizarComissao())}</strong>
                </td>
              </tr>
            </tfoot>
            </table>
          </TableScrollContainer>
        </div>
      </div>

      <div className="relatorio-section">
        <h2>Análise do Período</h2>
        <div className="analise-grid">
          <div className="analise-card">
            <h3>Ticket Médio</h3>
            <p className="valor">
              {formatCurrency(
                faturamento.length > 0
                  ? (totalizarFaturamento() / faturamento.reduce((sum, item) => sum + item.total_servicos, 0))
                  : 0
              )}
            </p>
          </div>
          <div className="analise-card">
            <h3>Total de Serviços</h3>
            <p className="valor">
              {faturamento.reduce((sum, item) => sum + item.total_servicos, 0)}
            </p>
          </div>
          <div className="analise-card">
            <h3>Margem Líquida (%)</h3>
            <p className="valor">
              {lucro && lucro.total_receita > 0
                ? ((lucro.lucro_liquido / lucro.total_receita) * 100).toFixed(1)
                : '0'
              }%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Relatorios;
